import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from 'lucide-react';
import type { FreimeldungPublicItem } from '../../../services/freimeldungPublicService';
import { nadelNeigungen } from '../../../services/planNadelNeigung';
import { PlanNadel, nadelAbstandPx } from '../../plaene/PlanNadel';
import {
  naechsterPlanZoom,
  nummerText,
  PLAN_ZOOM_START,
  PLAN_ZOOM_STUFEN,
  scrollAufPunkt,
  type FreimeldungPlanAnsicht as Ansicht,
  type FreimeldungTexte,
} from './freimeldungLogic';

const VERLAUF_STATE = { osiaFreimeldungPlan: '1' };

type BildZustand = 'laedt' | 'bereit' | 'fehler';

const FOKUSSIERBAR = 'button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Tab/Umschalt+Tab am Rand springt ans andere Ende (wie MangelKatalogDialog). */
const fokusFalle = (e: KeyboardEvent, panel: HTMLElement): void => {
  const alle = Array.from(panel.querySelectorAll<HTMLElement>(FOKUSSIERBAR)).filter(
    (el) => el.getClientRects().length > 0,
  );
  if (!alle.length) return;
  const erstes = alle[0];
  const letztes = alle[alle.length - 1];
  const aktiv = document.activeElement;
  const drin = aktiv instanceof Element && panel.contains(aktiv) && aktiv !== panel;
  if (drin && !alle.includes(aktiv as HTMLElement)) {
    // Fokus auf einem gerade deaktivierten Knopf (Zoomgrenze, „Ganzer Plan“):
    // zum Nachbarn, sonst liefe Tab aus dem Dialog.
    e.preventDefault();
    const danach = alle.filter((el) => aktiv.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
    const davor = alle.filter((el) => aktiv.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING);
    (e.shiftKey ? davor[davor.length - 1] ?? letztes : danach[0] ?? erstes).focus();
    return;
  }
  if (e.shiftKey && (!drin || aktiv === erstes)) {
    e.preventDefault();
    letztes.focus();
  } else if (!e.shiftKey && (!drin || aktiv === letztes)) {
    e.preventDefault();
    erstes.focus();
  }
};

/** Erste Nadel eines Plans als Anteil 0–1 — dort startet die Ansicht. */
const ersteNadelMitte = (ansicht: Ansicht | undefined): { x: number; y: number } => {
  const pin = ansicht?.pins[0];
  return pin ? { x: pin.x / 100, y: pin.y / 100 } : { x: 0.5, y: 0.5 };
};

/**
 * Vollbild-Plan zu EINEM Mangel auf der oeffentlichen Melderseite
 * (docs/freimeldung-plan-anzeige-plan.md). Zeigt nur die Nadeln dieses
 * Mangels; das Planbild laedt erst hier (Datenvolumen am Handy).
 *
 * Zoom ueber die BREITE (nicht per transform), damit die Nadeln gleich gross
 * bleiben und das Bild beim Vergroessern scharf neu gerechnet wird. Start bei
 * PLAN_ZOOM_START auf der ersten Nadel; ein Zoomschritt haelt die Mitte des
 * Sichtfensters fest.
 *
 * Liegt per Portal an body (sonst stapelt sie unter einem Elternteil mit
 * eigenem Stapelkontext, Lehre 3b0af00). Schliesst mit X, Esc und der
 * Zurueck-Geste (Verlaufseintrag wie NotificationSheet).
 */
export const FreimeldungPlanAnsicht: React.FC<{
  item: FreimeldungPublicItem;
  ansichten: Ansicht[];
  texte: FreimeldungTexte;
  onSchliessen: () => void;
}> = ({ item, ansichten, texte, onSchliessen }) => {
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState(PLAN_ZOOM_START);
  // Ladezustand GEBUNDEN an die Plan-Id: nach einem Planwechsel gilt „laedt“,
  // ohne einen Ruecksetz-Effekt, der mit einem schnellen onLoad wettliefe.
  const [bild, setBild] = useState<{ planId: string; zustand: BildZustand; verhaeltnis: number | null } | null>(null);
  const [sichtBreite, setSichtBreite] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const sichtRef = useRef<HTMLDivElement>(null);
  const flaecheRef = useRef<HTMLDivElement>(null);
  // Punkt (Anteil 0–1 der Planflaeche), der nach Laden/Zoom in der Mitte steht.
  const mitteRef = useRef(ersteNadelMitte(ansichten[0]));

  const onSchliessenRef = useRef(onSchliessen);
  useEffect(() => { onSchliessenRef.current = onSchliessen; }, [onSchliessen]);

  useEffect(() => {
    let durchZurueck = false;
    // Fokus in den Dialog, beim Schliessen zurueck auf den Ausloeser — sonst
    // liefe Tab weiter durch das verdeckte Formular darunter.
    const vorherFokus = document.activeElement;
    panelRef.current?.focus({ preventScroll: true });
    try { window.history.pushState(VERLAUF_STATE, ''); } catch { /* Verlauf gesperrt — dann nur Esc/X */ }
    const onPop = () => { durchZurueck = true; onSchliessenRef.current(); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSchliessenRef.current();
      else if (e.key === 'Tab' && panelRef.current) fokusFalle(e, panelRef.current);
    };
    window.addEventListener('popstate', onPop);
    document.addEventListener('keydown', onKey);
    const vorherOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = vorherOverflow;
      const st = window.history.state as { osiaFreimeldungPlan?: string } | null;
      if (!durchZurueck && st?.osiaFreimeldungPlan === VERLAUF_STATE.osiaFreimeldungPlan) {
        try { window.history.back(); } catch { /* egal */ }
      }
      if (vorherFokus instanceof HTMLElement && vorherFokus.isConnected) vorherFokus.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const messen = () => setSichtBreite(sichtRef.current?.clientWidth ?? 0);
    messen();
    window.addEventListener('resize', messen);
    return () => window.removeEventListener('resize', messen);
  }, []);

  const ansicht = ansichten[Math.min(index, ansichten.length - 1)];
  const planId = ansicht?.plan.id ?? '';
  const bildZustand: BildZustand = bild && bild.planId === planId ? bild.zustand : 'laedt';
  const seitenVerhaeltnis = bild && bild.planId === planId ? bild.verhaeltnis : null;

  useLayoutEffect(() => {
    const sicht = sichtRef.current;
    const flaeche = flaecheRef.current;
    if (bildZustand !== 'bereit' || !sicht || !flaeche) return;
    const ziel = scrollAufPunkt(
      mitteRef.current,
      { breite: flaeche.offsetWidth, hoehe: flaeche.offsetHeight },
      { breite: sicht.clientWidth, hoehe: sicht.clientHeight },
    );
    sicht.scrollLeft = ziel.left;
    sicht.scrollTop = ziel.top;
  }, [bildZustand, zoom, sichtBreite, planId]);

  if (!ansicht) return null;

  // Neuer Plan: von vorn, auf seiner ersten Nadel.
  const wechsle = (richtung: 1 | -1) => {
    const neu = (index + richtung + ansichten.length) % ansichten.length;
    mitteRef.current = ersteNadelMitte(ansichten[neu]);
    setZoom(PLAN_ZOOM_START);
    setIndex(neu);
  };

  const zoomen = (richtung: 1 | -1) => {
    const sicht = sichtRef.current;
    const flaeche = flaecheRef.current;
    if (sicht && flaeche && flaeche.offsetWidth > 0 && flaeche.offsetHeight > 0) {
      mitteRef.current = {
        x: (sicht.scrollLeft + sicht.clientWidth / 2) / flaeche.offsetWidth,
        y: (sicht.scrollTop + sicht.clientHeight / 2) / flaeche.offsetHeight,
      };
    }
    setZoom((z) => naechsterPlanZoom(z, richtung));
  };

  const ganzerPlan = () => {
    mitteRef.current = { x: 0.5, y: 0.5 };
    setZoom(PLAN_ZOOM_STUFEN[0]);
  };

  // Nahe Nadeln faechern auf — gerechnet in Bildschirm-Pixeln des gezeichneten
  // Plans (die Kugeln sind bei jedem Zoom gleich gross). Vor dem Laden A4 quer.
  const planBreitePx = (sichtBreite || 360) * zoom;
  const planHoehePx = planBreitePx / (seitenVerhaeltnis && seitenVerhaeltnis > 0 ? seitenVerhaeltnis : Math.SQRT2);
  const neigungen = nadelNeigungen(
    ansicht.pins.map((pin) => ({ x: (pin.x / 100) * planBreitePx, y: (pin.y / 100) * planHoehePx })),
    nadelAbstandPx('normal'),
  );

  const nummer = nummerText(item);
  const kleinsterZoom = zoom <= PLAN_ZOOM_STUFEN[0] + 1e-9;
  const groessterZoom = zoom >= PLAN_ZOOM_STUFEN[PLAN_ZOOM_STUFEN.length - 1] - 1e-9;
  const knopf =
    'inline-flex items-center justify-center min-w-[48px] min-h-[48px] px-3 rounded-xl text-base font-semibold ' +
    'text-white bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed';

  const inhalt = (
    <div
      ref={panelRef}
      tabIndex={-1}
      className="fixed inset-0 z-[1000] flex flex-col bg-slate-900 text-white outline-none"
      role="dialog"
      aria-modal="true"
      aria-label={`${nummer} ${item.title}`.trim()}
    >
      <div className="flex items-start gap-3 px-4 pt-3 pb-2" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-base break-words">
            {nummer && <span className="mr-2">{nummer}</span>}
            {item.title}
          </p>
          <p className="text-sm text-slate-300 break-words">
            {ansicht.plan.name}
            {ansichten.length > 1 && ` · ${texte.planVon(index + 1, ansichten.length)}`}
          </p>
        </div>
        <button type="button" onClick={onSchliessen} className={knopf} aria-label={texte.planSchliessen}>
          <X className="w-6 h-6" aria-hidden="true" />
        </button>
      </div>

      <div ref={sichtRef} className="relative flex-1 overflow-auto overscroll-contain bg-slate-800">
        <div className="flex min-h-full">
          <div
            ref={flaecheRef}
            className="relative m-auto shrink-0 bg-white"
            style={{ width: `${zoom * 100}%`, isolation: 'isolate' }}
          >
            <img
              key={ansicht.plan.id}
              src={ansicht.plan.bildUrl}
              alt={ansicht.plan.name}
              decoding="async"
              draggable={false}
              className="block w-full h-auto select-none"
              onLoad={(e) => {
                const img = e.currentTarget;
                const verhaeltnis = img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : null;
                setBild({ planId, zustand: 'bereit', verhaeltnis });
              }}
              onError={() => setBild({ planId, zustand: 'fehler', verhaeltnis: null })}
            />
            {bildZustand === 'bereit' &&
              ansicht.pins.map((pin, i) => (
                <PlanNadel
                  key={`${pin.x}-${pin.y}-${i}`}
                  x={pin.x}
                  y={pin.y}
                  nummer={item.ticketNumber}
                  neigung={neigungen[i]}
                  title={nummer || undefined}
                />
              ))}
          </div>
        </div>
        {bildZustand !== 'bereit' && (
          <p className="absolute inset-x-0 top-1/3 text-center text-base text-slate-200 px-4" aria-live="polite">
            {bildZustand === 'fehler' ? texte.planFehler : texte.planLaedt}
          </p>
        )}
      </div>

      <div
        className="flex flex-wrap items-center justify-center gap-2 px-4 pt-2"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        {ansichten.length > 1 && (
          <button
            type="button"
            className={knopf}
            onClick={() => wechsle(-1)}
            aria-label={texte.planVorheriger}
          >
            <ChevronLeft className="w-6 h-6" aria-hidden="true" />
          </button>
        )}
        <button type="button" className={knopf} onClick={() => zoomen(-1)} disabled={kleinsterZoom} aria-label={texte.planKleiner}>
          <ZoomOut className="w-6 h-6" aria-hidden="true" />
        </button>
        <button type="button" className={knopf} onClick={ganzerPlan} disabled={kleinsterZoom}>
          {texte.planGanz}
        </button>
        <button type="button" className={knopf} onClick={() => zoomen(1)} disabled={groessterZoom} aria-label={texte.planGroesser}>
          <ZoomIn className="w-6 h-6" aria-hidden="true" />
        </button>
        {ansichten.length > 1 && (
          <button
            type="button"
            className={knopf}
            onClick={() => wechsle(1)}
            aria-label={texte.planNaechster}
          >
            <ChevronRight className="w-6 h-6" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(inhalt, document.body) : inhalt;
};
